<template>
  <v-dialog
    v-if="isMobile"
    v-model="modalState"
    max-width="480px"
    content-class="fixed-height-md"
  >
    <modal-card title="Filters" fixed @close="modalState = false">
      <div class="pa-4 report-filters">
        <template v-for="(config, key) in report.controls">
          <div v-if="controlComponent(key, config)" :key="key" class="mb-3">
            <component
              :schema="controlSchema(config)"
              :is="controlComponent(key, config)"
              :value="inputValue(config)"
              @input="setInput(config, $event)"
            />
          </div>
        </template>
      </div>

      <template v-slot:actions>
        <v-btn color="appMain" @click="reload" :disabled="!canUpdate">
          Update
        </v-btn>
      </template>
    </modal-card>
  </v-dialog>

  <div v-else>
    <h5 class="mt-4 ml-4">Filters</h5>
    <div class="pa-4 report-filters">
      <template v-for="(config, key) in report.controls">
        <div :key="key" v-if="controlComponent(key, config)" class="mb-3">
          <component
            :schema="controlSchema(config)"
            :is="controlComponent(key, config)"
            @input="setInput(config, $event)"
            :value="inputValue(config)"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import ReportTrackerNames from './controls/ReportTrackerNames';
import ReportGroups from './controls/ReportGroups';
import ReportContactField from './controls/ReportContactField';
import ReportCampaign from './controls/ReportCampaign';
import ReportOptionList from './controls/ReportOptionList';
import ReportContacts from './controls/ReportContacts';
import ReportCustomObjects from './controls/ReportCustomObjects';
import ReportTimeframe from './controls/ReportTimeframe';
import ReportOfficePattern from './controls/ReportOfficePattern';
import ReportObject from './controls/ReportObject';

import DateField from '@/components/formFields/DateField';
import TextField from '@/components/formFields/TextField';
import NumberField from '@/components/formFields/NumberField';
import CheckboxField from '@/components/formFields/CheckboxField';
import UserListField from '@/components/formFields/UserListField';

export default {
  name: 'ReportControls',
  components: {
    ReportTrackerNames,
    ReportGroups,
    ReportContactField,
    ReportCampaign,
    DateField,
    TextField,
    NumberField,
    CheckboxField,
    ReportOptionList,
    ReportContacts,
    ReportCustomObjects,
    UserListField,
    ReportTimeframe,
    ReportOfficePattern,
    ReportObject,
  },
  props: {
    value: {},
    filters: {
      type: Object,
      default: () => ({}),
    },
    canUpdate: {
      type: Boolean,
      default: false,
    },
    report: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      schemaFields: [
        'min',
        'max',
        'options',
        'default',
        'placeholder',
        'start_min',
        'start_max',
        'end_min',
        'end_max',
        'all_time',
        'pid',
        'params',
        'meta',
      ],
    };
  },

  computed: {
    modalState: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
  },

  methods: {
    inputValue(config) {
      let v = this.filters[config.param];

      if (config.type === 'bool') {
        v = !!(v === 'false' ? false : v || config.default);
      }

      if (typeof v !== 'undefined') {
        return v;
      }

      return '';
    },

    controlSchema(config) {
      const schema = {
        title: config.title,
        label: config.title,
        help: config.desc,
        dark: !this.isMobile,
        multiple: !!config.multiple,
      };

      if (config?.campaignType) {
        schema.campaignType = config.campaignType;
      }

      this.schemaFields.forEach((schemaField) => {
        if (typeof config[schemaField] !== 'undefined') {
          schema[schemaField] = config[schemaField];
        }
      });

      return schema;
    },

    controlComponent(key, config) {
      if (config.hidden) {
        return '';
      }

      if (key.startsWith('tracker_name')) {
        return 'ReportTrackerNames';
      }

      if (key === 'group' || key === 'groups') {
        return 'ReportGroups';
      }

      if (key === 'group_by' || key === 'field' || key === 'fields') {
        return 'ReportContactField';
      }

      if (key === 'campaign') {
        return 'ReportCampaign';
      }

      if (key === 'custom_object') {
        return 'ReportCustomObjects';
      }

      if (key === 'contact') {
        return 'ReportContacts';
      }

      if (config.type === 'date') {
        return 'DateField';
      }

      if (config.type === 'object') {
        return 'ReportObject';
      }

      // vector specific field
      if (key === 'office_pattern') {
        return 'ReportOfficePattern';
      }

      // right now, this is a vector specific report type.
      // was too difficult to find a way to make this all work on the backend via the plugin
      // so easier to include as support on the frontend
      if (config.type === 'timeframe') {
        return 'ReportTimeframe';
      }

      if (config.options) {
        return 'ReportOptionList';
      }

      if (config.type === 'string') {
        return 'TextField';
      }

      if (['start_time', 'end_time'].includes(key)) {
        return 'TextField';
      }

      if (config.type === 'bool') {
        return 'CheckboxField';
      }

      if (key === 'user') {
        return 'UserListField';
      }

      if (config.type === 'int') {
        return 'NumberField';
      }

      return '';
    },

    setInput(config, event) {
      let param = config.param;
      let value = event;

      // allows for the report control itself to pass a param
      // that is different thatn the configuration.
      // this helps for for controls that set multiple params.
      if (event && typeof event === 'object' && event.param) {
        param = event.param;
        value = event.value;
      }

      if (param && value !== undefined) {
        this.checkDateFilterChanges(param, value);
        this.$emit('updateInput', param, value);
      }
    },

    // reset date filter if start_date is greater than end_date
    checkDateFilterChanges(param, value) {
      const isDateFilter = ['start_date', 'end_date'].includes(param);
      if (!isDateFilter || !value) return;

      const isStartDate = param === 'start_date';
      const dateToCheckType = isStartDate ? 'end_date' : 'start_date';

      if (!this.filters[dateToCheckType]) return;

      const currentDate = new Date(value);
      const dateToCheck = new Date(this.filters[dateToCheckType]);

      const shouldResetDate = isStartDate
        ? currentDate > dateToCheck
        : currentDate < dateToCheck;

      if (shouldResetDate) {
        this.$emit('updateInput', dateToCheckType, '');
      }
    },

    reload() {
      this.$emit('reload');
      this.modalState = false;
    },
  },
};
</script>

<style lang="less">
.update-report {
  position: sticky;
  bottom: 10px;
}

.report-filters .field__inner-title {
  display: none;
}
</style>
